import { useCallback, useState } from 'react';
import { evaluateBookingGate } from './bookingGate';
import type {
  BookEventCreateResult,
  BookEventHostData,
  BookEventUser,
  EagreementsSnapshot,
  PaymentMethodOption,
} from './bookEvent.types';
import { isUserFirstBook, shouldUseInteractiveSpotBooking } from './bookEventRules';
import {
  bookingAnalytics,
  isBookAppointment,
  type BookingOrchestrationVariant,
} from './bookingDialogVariant';

function successfulBookingId(response: BookEventCreateResult | void): string | null {
  return response?.success === true ? response.Booking?._id || null : null;
}

function trackBookingError(
  data: BookEventHostData,
  variant: BookingOrchestrationVariant,
  payload?: string
) {
  const analytics = bookingAnalytics(variant);
  data.trackEvent({
    category: analytics.category,
    name: analytics.bookError,
    payload,
    metadata: analytics.includeEventMetadata ? { eventName: data.event.name } : undefined,
  });
}

async function stopForInitialGate(
  gate: ReturnType<typeof evaluateBookingGate>,
  data: BookEventHostData,
  collectPaymentCard: () => Promise<void>
): Promise<boolean> {
  switch (gate.kind) {
    case 'needsTerms':
      data.showTermsRequiredDialog();
      return true;
    case 'notEligible':
      data.showNotEligibleDialog();
      return true;
    case 'membershipExpired':
      await data.showGoPayg();
      return true;
    case 'needsCard':
      await collectPaymentCard();
      return true;
    case 'anonymous':
    case 'inFlight':
      return true;
    case 'ok':
      return false;
    default:
      return true;
  }
}

function isBlockedByEagreements(snapshot: EagreementsSnapshot): boolean {
  return (
    evaluateBookingGate({
      mode: 'book',
      eagreementsOnly: true,
      eagreements: {
        flagResolved: snapshot.flagResolved,
        mandatoryEnabled: snapshot.mandatoryEnabled,
        documentsSigned: snapshot.documentsSigned,
      },
    }).kind === 'blockedByEagreements'
  );
}

export function useBookEventActions(args: {
  data: BookEventHostData;
  variant: BookingOrchestrationVariant;
  user: BookEventUser | null;
  guests: number;
  priceToPay: number | null;
  creditsToPay: number;
  selectedPayment: PaymentMethodOption | undefined;
  collectPaymentCard: () => Promise<void>;
  refreshEagreements: (options?: {
    forceRefresh?: boolean;
  }) => Promise<EagreementsSnapshot>;
}) {
  const {
    data,
    variant,
    user,
    guests,
    priceToPay,
    creditsToPay,
    selectedPayment,
    collectPaymentCard,
    refreshEagreements,
  } = args;
  const event = data.event;
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [messagesData, setMessagesData] = useState<Record<string, unknown>>({});
  const [transactionInProgress, setTransactionInProgress] = useState(false);

  const recordFailedResponse = useCallback(
    (response: BookEventCreateResult | void) => {
      setError(true);
      setSuccess(false);
      setErrors(response?.errors || []);
      setMessagesData(response?.message_data || {});
      trackBookingError(data, variant, response?.message);
    },
    [data, variant]
  );

  const recordThrownFailure = useCallback(() => {
    setError(true);
    setSuccess(false);
    setErrors([]);
    setMessagesData({});
    trackBookingError(data, variant, 'none');
  }, [data, variant]);

  const submitBooking = useCallback(
    async (bookingGuests: number) => {
      if (!user?._id) {
        return;
      }
      try {
        const response = await data.createBooking({
          userId: user._id,
          event,
          payment: selectedPayment,
          guests: bookingGuests,
        });
        if (response?.success === true) {
          data.trackBooking();
          setError(false);
          setSuccess(true);
          const analytics = bookingAnalytics(variant);
          data.trackEvent({
            category: analytics.category,
            name: analytics.bookSuccess,
            metadata: analytics.includeEventMetadata
              ? {
                  eventName: event.name,
                  activeMembership: user.membership?.status === 'ACTIVE',
                  price: priceToPay === null ? 0 : priceToPay,
                }
              : undefined,
          });
          if (!isBookAppointment(variant)) {
            data.forceReloadClassesView();
          }
        } else {
          recordFailedResponse(response);
        }
      } catch {
        recordThrownFailure();
      } finally {
        setTransactionInProgress(false);
      }
    },
    [
      data,
      event,
      priceToPay,
      recordFailedResponse,
      recordThrownFailure,
      selectedPayment,
      user,
      variant,
    ]
  );

  const createBookingThenShowSpotDialog = useCallback(async () => {
    if (!user?._id) {
      return;
    }
    setTransactionInProgress(true);
    setError(false);
    setErrors([]);
    setMessagesData({});
    try {
      const response = await data.createBooking({
        userId: user._id,
        event,
        payment: selectedPayment,
        guests,
        options: { skipUserRefresh: true },
      });
      const bookingId = successfulBookingId(response);
      if (bookingId) {
        try {
          await data.showBookSpot(bookingId, guests);
        } catch {
          setTransactionInProgress(false);
          recordThrownFailure();
        }
        return;
      }
      setTransactionInProgress(false);
      recordFailedResponse(response);
    } catch {
      setTransactionInProgress(false);
      recordThrownFailure();
    }
  }, [data, event, guests, recordFailedResponse, recordThrownFailure, selectedPayment, user]);

  const bookEvent = useCallback(async () => {
    const analytics = bookingAnalytics(variant);
    data.trackEvent({
      category: analytics.category,
      name: analytics.showLoggedIn,
    });
    const gate = evaluateBookingGate({
      mode: 'book',
      user,
      event,
      payment: { selected: selectedPayment },
      priceToPay,
      creditsToPay,
      termsAccepted,
      isFirstBook: isUserFirstBook(user),
      transactionInProgress,
      formatExpiryLocal: (timestamp) => data.formatUnix(timestamp, 'YYYY-MM-DDTHH:mm:ss'),
      getBranchLocalTime: data.getBranchLocalTime,
    });
    if (await stopForInitialGate(gate, data, collectPaymentCard)) {
      return;
    }

    const bookingGuests = guests;
    setTransactionInProgress(true);
    const snapshot = await refreshEagreements({ forceRefresh: true });
    if (isBlockedByEagreements(snapshot)) {
      setTransactionInProgress(false);
      return;
    }

    const useSpotDialog =
      !isBookAppointment(variant) &&
      shouldUseInteractiveSpotBooking({
        event,
        branch: data.branch,
        isInteractiveSpotBookingEnabled: data.flags.isInteractiveSpotBookingEnabled,
        isSpotBookingEnabledForEvent: data.isSpotBookingEnabledForEvent(event),
      });
    if (useSpotDialog) {
      await createBookingThenShowSpotDialog();
      return;
    }
    await submitBooking(bookingGuests);
  }, [
    collectPaymentCard,
    createBookingThenShowSpotDialog,
    creditsToPay,
    data,
    event,
    guests,
    priceToPay,
    refreshEagreements,
    selectedPayment,
    submitBooking,
    termsAccepted,
    transactionInProgress,
    user,
    variant,
  ]);

  const tryAgain = useCallback(() => {
    setError(false);
    setSuccess(false);
  }, []);

  return {
    termsAccepted,
    setTermsAccepted,
    success,
    error,
    errors,
    messagesData,
    transactionInProgress,
    bookEvent,
    tryAgain,
  };
}
